<template>
  <div class="device" :class="{ 'device--focus': isFocused, disabled: disabled }">
    <div class="device__bar">
      <div class="device__player-number">{{ player.playerNumber }}</div>
      <div>
        <span v-if="player.firstname">{{ player.firstname }}</span>&nbsp;
        <span v-if="player.lastname">{{ player.lastname }}</span>
        <span v-else-if="player.specialName">{{ player.specialName }}</span>
      </div>
    </div>
    <transition name="roll">
      <template v-if="isFocused">
        <div class="device__details">
          <div class="device__info-track">
            <KeepAlive><idle-status darkmode :deviceId="device.id" /></KeepAlive>
            <gps-status darkmode :device="device.id" />
            <battery-status darkmode :device="device.id" />
          </div>
          <div class="device__info-player">
            <div class="device__info-inline">
              Team: <span v-if="player.clubname">{{ player.clubname }}</span>
            </div>
            <div class="device__info-inline">
              Dystans: <span v-if="player.classification">{{ classification.namePl }}</span>
            </div>
          </div>
        </div>
      </template>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import {
  UPDATE_AWARDED_DEVICES,
  READ_ROUTE,
  READ_SUMMARY_DEVICE,
} from '@/store/actions.type'
import { SET_FOCUS_DEVICE_ID } from '@/store/mutations.type'
import moment from 'moment'
import BatteryStatus from '@/components/device/BatteryStatus'
import GpsStatus from '@/components/device/GpsStatus.vue'
import IdleStatus from '@/components/device/IdleStatus.vue'
export default {
  components: {
    BatteryStatus,
    IdleStatus,
    GpsStatus,
  },
  data() {
    return {
      isFetchingDistance: false,
    }
  },
  props: {
    device: {
      type: Object,
      required: true,
    },
    player: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters([
      'awardedDeviceIds',
      'focusDeviceId',
      'markers',
      'devicesSummary',
      'classifications',
      'competitions',
      'isPlaybackReady',
      'playbackMarkers',
      'isShowAllNames',
    ]),
    isFocused() {
      return this.focusDeviceId == this.device.id
    },
    deviceDistance() {
      return this.devicesSummary[this.device.id].distance
    },

    classification() {
      if (this.player.specialName) {
        return null
      }
      return this.classifications[this.player.classification]
    },
    competition() {
      if (this.classification) {
        return this.competitions[this.classification.competition]
      }
      return null
    },
    playbackMarker() {
      return this.playbackMarkers[this.device.id]
    },
    liveMarker() {
      return this.markers[this.device.id]
    },
  },
  methods: {
    ...mapActions({
      UPDATE_AWARDED_DEVICES,
      READ_ROUTE,
      READ_SUMMARY_DEVICE,
    }),
    ...mapMutations({
      SET_FOCUS_DEVICE_ID,
    }),
    now() {
      return moment()
    },
    async refreshDistance() {
      this.isFetchingDistance = true
      let fromTime = this.competition.startedTime.clone()
      await this.READ_SUMMARY_DEVICE({
        from: fromTime.subtract(2, 'h').toISOString(),
        to: new Date().toISOString(),
        deviceId: this.device.id,
      })
      this.isFetchingDistance = false
    },
    async downloadGPX() {
      let fromTime = this.competition.startedTime.clone()
      let query = {
        from: fromTime.subtract(2, 'h').toISOString(),
        to: new Date().toISOString(),
        deviceId: this.device.id,
      }
      let route = await this.READ_ROUTE(query)
      let blob = new Blob([route], { type: 'text/plain' })
      let element = document.createElement('a')
      element.download = `${this.player.firstname} ${this.player.lastname}.gpx`
      element.href = window.URL.createObjectURL(blob)
      element.click()
    },
    onDeviceClick() {
      if (this.focusDeviceId == this.device.id) {
        this.SET_FOCUS_DEVICE_ID(null)
      } else {
        this.SET_FOCUS_DEVICE_ID(this.device.id)
      }
      // this.$emit('changeFocus')
    },
    showPopup() {
      if (this.isPlaybackReady) {
        this.playbackMarker.openTooltip()
      } else {
        this.liveMarker.openPopup()
      }
    },
    hidePopup() {
      if (this.isPlaybackReady) {
        this.playbackMarker.closeTooltip()
      } else {
        this.liveMarker.closePopup()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.disabled {
  opacity: 0.6;
}

.device {
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  box-shadow: 0 8px 12px 0 rgba(0, 0, 0, 0.1), 0 0 2px 0 rgba(55, 71, 79, 0.2);
  background-color: $white;
  padding: 3px;
  line-height: 1.43;
  transition:
    color .5s, background-color 2s;

  &:hover:not(.device--focus):not(.disabled) {
    background-color: $light-grey;
  }

  &--focus {
    background-color: #37474f;
    color: #edf0f1;
    max-height: 190px;
  }

  &__bar {
    padding: 0 8px;
    display: flex;
    gap: 10px;
  }

  &__info {
    &-inline {
      span {
        font-weight: 700;
      }
    }
  }

  &__details {
    // background-color: #546e7a;
    // height: 200px;
    width: 100%;
    background-color: #546e7a;
    padding: 0 5px;
  }

  &__info {
    width: 100%;
    padding-left: 16px;
    cursor: pointer;

    &-track {
      padding: 2px;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-evenly;
      border-bottom: 2px solid #37474f;
    }

    &-track--right {
      display: flex;
      justify-content: flex-end;
    }
  }

  &__player-number {
    font-weight: bold;
  }

  &__switch {
    height: 100%;

    &-btn {
      width: 40px;
      height: 24px;
      background-color: $pale-grey;
      border-radius: 12px;
      position: relative;
      cursor: pointer;
      transition: all 0.3s;

      &::after {
        transition: all 0.3s;
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        border-radius: 12px;
        display: inline-block;
        background-color: $white;
        box-shadow: 0 8px 12px 0 rgba(0, 0, 0, 0.1),
          0 0 2px 0 rgba(55, 71, 79, 0.2);
      }

      &--active {
        background-color: $red-orange;
        opacity: 1;

        &::after {
          left: 18px;
        }
      }
    }
  }
}

.roll-enter {
  max-height: 0;
}

.roll-enter-to {
  max-height: 200px;
}

.roll-leave {
  max-height: 200px;
}

.roll-leave-to {
  max-height: 0;
}

.roll-enter-active,
.roll-leave-active {
  transition: all 0.8s;
  overflow: hidden;
}
</style>
